// Validation utilities for forms

/**
 * Validate email address
 * @param {string} email - Email address to validate
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateEmail = (email) => {
  if (!email || !email.trim()) {
    return {
      isValid: false,
      error: 'Email is required'
    };
  }

  // RFC 5322 compliant email regex (simplified)
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  
  if (!emailRegex.test(email.trim())) {
    return {
      isValid: false,
      error: 'Please enter a valid email address'
    };
  }

  // Check for common typos
  const commonDomains = ['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'aol.com'];
  const domain = email.split('@')[1]?.toLowerCase();
  
  if (domain && domain.includes('..')) {
    return {
      isValid: false,
      error: 'Email address contains invalid characters'
    };
  }

  return {
    isValid: true,
    error: ''
  };
};

/**
 * Validate phone number
 * @param {string} phoneNumber - Phone number to validate
 * @param {string} countryCode - Country dial code
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validatePhoneNumber = (phoneNumber, countryCode = '1') => {
  if (!phoneNumber || !phoneNumber.trim()) {
    return {
      isValid: false,
      error: 'Phone number is required'
    };
  }

  // Remove all non-numeric characters
  const numericPhone = phoneNumber.replace(/\D/g, '');

  if (numericPhone.length === 0) {
    return {
      isValid: false,
      error: 'Phone number must contain digits'
    };
  }

  // Validation based on country code
  const validationRules = {
    '1': { min: 10, max: 10, name: 'US/Canada' }, // US/Canada
    '44': { min: 10, max: 10, name: 'UK' }, // UK
    '91': { min: 10, max: 10, name: 'India' }, // India
    '92': { min: 10, max: 10, name: 'Pakistan' }, // Pakistan
    '61': { min: 9, max: 9, name: 'Australia' }, // Australia
    '81': { min: 10, max: 10, name: 'Japan' }, // Japan
    '86': { min: 11, max: 11, name: 'China' }, // China
    '49': { min: 10, max: 11, name: 'Germany' }, // Germany
    '33': { min: 9, max: 9, name: 'France' }, // France
    '971': { min: 9, max: 9, name: 'UAE' }, // UAE
    '65': { min: 8, max: 8, name: 'Singapore' }, // Singapore
  };

  const rule = validationRules[countryCode];
  
  if (rule) {
    if (numericPhone.length < rule.min) {
      return {
        isValid: false,
        error: `Phone number must be at least ${rule.min} digits for ${rule.name}`
      };
    }
    if (numericPhone.length > rule.max) {
      return {
        isValid: false,
        error: `Phone number must not exceed ${rule.max} digits for ${rule.name}`
      };
    }
  } else {
    // Generic validation for other countries
    if (numericPhone.length < 7) {
      return {
        isValid: false,
        error: 'Phone number is too short (minimum 7 digits)'
      };
    }
    if (numericPhone.length > 15) {
      return {
        isValid: false,
        error: 'Phone number is too long (maximum 15 digits)'
      };
    }
  }

  // Check for obviously invalid patterns
  if (/^0+$/.test(numericPhone)) {
    return {
      isValid: false,
      error: 'Phone number cannot be all zeros'
    };
  }

  if (/^1+$/.test(numericPhone)) {
    return {
      isValid: false,
      error: 'Phone number appears to be invalid'
    };
  }

  return {
    isValid: true,
    error: ''
  };
};

/**
 * Validate name
 * @param {string} name - Name to validate
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateName = (name) => {
  if (!name || !name.trim()) {
    return {
      isValid: false,
      error: 'Name is required'
    };
  }

  if (name.trim().length < 2) {
    return {
      isValid: false,
      error: 'Name must be at least 2 characters'
    };
  }

  if (name.trim().length > 100) {
    return {
      isValid: false,
      error: 'Name must not exceed 100 characters'
    };
  }

  // Check for valid name characters (letters, spaces, hyphens, apostrophes)
  const nameRegex = /^[a-zA-Z\s'-]+$/;
  if (!nameRegex.test(name.trim())) {
    return {
      isValid: false,
      error: 'Name can only contain letters, spaces, hyphens, and apostrophes'
    };
  }

  return {
    isValid: true,
    error: ''
  };
};

/**
 * Validate description/message
 * @param {string} description - Description to validate
 * @param {number} minLength - Minimum length (default: 10)
 * @param {number} maxLength - Maximum length (default: 1000)
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateDescription = (description, minLength = 10, maxLength = 1000) => {
  if (!description || !description.trim()) {
    return {
      isValid: false,
      error: 'Description is required'
    };
  }

  if (description.trim().length < minLength) {
    return {
      isValid: false,
      error: `Description must be at least ${minLength} characters`
    };
  }

  if (description.trim().length > maxLength) {
    return {
      isValid: false,
      error: `Description must not exceed ${maxLength} characters`
    };
  }

  return {
    isValid: true,
    error: ''
  };
};

/**
 * Validate entire form
 * @param {Object} formData - Form data object
 * @param {string} formData.name - Name
 * @param {string} formData.email - Email
 * @param {string} formData.phone - Phone number
 * @param {string} formData.countryCode - Country code
 * @param {string} formData.description - Description
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export const validateContactForm = (formData) => {
  const errors = {};

  // Validate name
  const nameValidation = validateName(formData.name);
  if (!nameValidation.isValid) {
    errors.name = nameValidation.error;
  }

  // Validate email
  const emailValidation = validateEmail(formData.email);
  if (!emailValidation.isValid) {
    errors.email = emailValidation.error;
  }

  // Validate phone
  const phoneValidation = validatePhoneNumber(formData.phone, formData.countryCode);
  if (!phoneValidation.isValid) {
    errors.phone = phoneValidation.error;
  }

  // Validate description
  const descriptionValidation = validateDescription(formData.description);
  if (!descriptionValidation.isValid) {
    errors.description = descriptionValidation.error;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Format phone number for display
 * @param {string} phoneNumber - Phone number to format
 * @param {string} countryCode - Country dial code
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (phoneNumber, countryCode = '1') => {
  const numericPhone = phoneNumber.replace(/\D/g, '');
  
  // US/Canada formatting
  if (countryCode === '1' && numericPhone.length === 10) {
    return `(${numericPhone.slice(0, 3)}) ${numericPhone.slice(3, 6)}-${numericPhone.slice(6)}`;
  }
  
  // Default formatting
  return numericPhone;
};
